import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import type { UserSummary } from '../types';
import '../styles/feature-flags.css';

interface FlagUser {
  id: string;
  name: string;
  email: string;
  featureFlags: string[];
}

export function AdminFeatureFlagsPage() {
  const { user } = useAuth();

  const [availableFlags, setAvailableFlags] = useState<string[]>([]);
  const [allFlagUsers, setAllFlagUsers] = useState<FlagUser[]>([]);
  const [selectedFlag, setSelectedFlag] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<UserSummary[]>([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canAccess =
    user?.role === 'admin' || user?.featureFlags?.includes('feature-flags-admin');

  const fetchFlags = useCallback(async () => {
    try {
      const result = await api.adminFeatureFlags();
      setAvailableFlags(result.availableFlags);
      setAllFlagUsers(result.users);
      if (!result.availableFlags.length) {
        setSelectedFlag(null);
      }
    } catch {
      setError('Unable to load feature flags.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canAccess) {
      void fetchFlags();
    } else {
      setLoading(false);
      setError('You do not have permission to manage feature flags.');
    }
  }, [canAccess, fetchFlags]);

  // Auto-select first flag
  useEffect(() => {
    if (!selectedFlag && availableFlags.length > 0) {
      setSelectedFlag(availableFlags[0]);
    }
  }, [availableFlags, selectedFlag]);

  // Debounced search
  useEffect(() => {
    if (!search.trim()) {
      setSearchResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await api.searchUsers(search.trim());
        setSearchResults(results);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  const handleAdd = useCallback(
    async (userId: string, flag: string) => {
      setBusy(true);
      try {
        await api.adminAddFeatureFlag(userId, flag);
        await fetchFlags();
      } catch (err) {
        alert((err as Error).message || 'Failed to add flag');
      } finally {
        setBusy(false);
      }
    },
    [fetchFlags]
  );

  const handleRemove = useCallback(
    async (userId: string, flag: string) => {
      setBusy(true);
      try {
        await api.adminRemoveFeatureFlag(userId, flag);
        await fetchFlags();
      } catch (err) {
        alert((err as Error).message || 'Failed to remove flag');
      } finally {
        setBusy(false);
      }
    },
    [fetchFlags]
  );

  const usersWithSelectedFlag = selectedFlag
    ? allFlagUsers.filter((u) => u.featureFlags.includes(selectedFlag))
    : [];

  const isSelfFlagAdmin = (userId: string, flag: string) =>
    flag === 'feature-flags-admin' && userId === user?.id;

  if (loading) {
    return (
      <div className="feature-flags">
        <div className="feature-flags__loading">Loading feature flags...</div>
      </div>
    );
  }

  if (error && !availableFlags.length) {
    return (
      <div className="feature-flags">
        <div className="feature-flags__error">{error}</div>
      </div>
    );
  }

  return (
    <div className="feature-flags">
      <header className="feature-flags__header">
        <h1>Feature Flags</h1>
        <p className="feature-flags__subtitle">
          Manage feature access for users across the platform.
        </p>
      </header>

      <div className="feature-flags__layout">
        {/* Left: feature list */}
        <aside className="feature-flags__sidebar">
          <h2 className="feature-flags__sidebar-title">Features</h2>
          <ul className="feature-flags__flag-list">
            {availableFlags.map((flag) => {
              const count = allFlagUsers.filter((u) =>
                u.featureFlags.includes(flag)
              ).length;
              return (
                <li key={flag}>
                  <button
                    type="button"
                    className={`feature-flags__flag-item${selectedFlag === flag ? ' is-active' : ''}`}
                    onClick={() => {
                      setSelectedFlag(flag);
                      setSearch('');
                      setSearchResults([]);
                    }}
                  >
                    <span className="feature-flags__flag-name">{flag}</span>
                    <span className="feature-flags__flag-count">
                      {count} user{count !== 1 ? 's' : ''}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Right: users with selected flag */}
        <main className="feature-flags__main">
          {selectedFlag ? (
            <>
              <div className="feature-flags__main-header">
                <h2>{selectedFlag}</h2>
                <input
                  type="text"
                  className="feature-flags__search"
                  placeholder="Search users to grant access..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* Search results */}
              {search.trim() && (
                <div className="feature-flags__search-results">
                  {searching ? (
                    <div className="feature-flags__empty-small">Searching...</div>
                  ) : searchResults.length > 0 ? (
                    <div className="feature-flags__user-list">
                      {searchResults.map((u) => {
                        const hasFlag = allFlagUsers.some(
                          (fu) =>
                            fu.id === u.id &&
                            fu.featureFlags.includes(selectedFlag)
                        );
                        return (
                          <div key={u.id} className="feature-flags__user-row">
                            <div className="feature-flags__user-info">
                              <span className="feature-flags__user-name">
                                {u.name}
                              </span>
                              <span className="feature-flags__user-email">
                                {u.email}
                              </span>
                            </div>
                            {hasFlag ? (
                              <span className="feature-flags__granted-badge">
                                Granted
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="feature-flags__action-btn feature-flags__action-btn--add"
                                onClick={() => handleAdd(u.id, selectedFlag)}
                                disabled={busy}
                              >
                                + Grant access
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="feature-flags__empty-small">
                      No users found.
                    </div>
                  )}
                </div>
              )}

              {/* Current users with this flag */}
              <div className="feature-flags__section-label">
                Users with access ({usersWithSelectedFlag.length})
              </div>
              {usersWithSelectedFlag.length > 0 ? (
                <div className="feature-flags__user-list">
                  {usersWithSelectedFlag.map((u) => (
                    <div key={u.id} className="feature-flags__user-row">
                      <div className="feature-flags__user-info">
                        <span className="feature-flags__user-name">
                          {u.name}
                        </span>
                        <span className="feature-flags__user-email">
                          {u.email}
                        </span>
                      </div>
                      {isSelfFlagAdmin(u.id, selectedFlag) ? (
                        <span
                          className="feature-flags__self-badge"
                          title="You cannot remove your own flag management access"
                        >
                          You (protected)
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="feature-flags__action-btn feature-flags__action-btn--remove"
                          onClick={() => handleRemove(u.id, selectedFlag)}
                          disabled={busy}
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="feature-flags__empty">
                  No users have this feature flag yet. Search above to grant
                  access.
                </div>
              )}
            </>
          ) : (
            <div className="feature-flags__empty">
              Select a feature from the sidebar.
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
